<template>
	<div class="stats-settlement">
		<div class="settlement-name">
			<span class="label"> {{ translate("ui.town_name") }}: </span>
			<EditableButton
				:text="settlementName"
				@click="openSettlementNameEditPopup"
			/>
		</div>
		<HorizontalDecorDivider />
		<div class="settlement-stats-table-wrapper" ref="settlementTable">
			<Table
				class="settlement-stats-table"
				:class="{ 'settlement-stats-table--is-max': isMax }"
			>
				<template #decoration>
					<VerticalDecorHighlighter
						:width="dynamicTableDecoratorWidth"
						:height="dynamicTableDecoratorHeight"
						:left="dynamicTableDecorationInnerLeft"
					/>
				</template>
				<template #tbody>
					<tr v-for="(row, idx) in tableData" :key="idx">
						<td class="settlement-stats-table__name">
							<ShrinkText
								v-if="row.name"
								:text="row.name"
								:textAlign="TextAlign.LEFT"
								:isBold="idx === 0"
								:fontSize="14"
							/>
						</td>
						<td
							v-if="row?.col1"
							class="settlement-stats-table__value col1"
						>
							<div v-if="idx === 0">
								<div class="icon-tile-selector">
									<span>{{ row.col1 }}</span>
								</div>
							</div>
							<div v-else>
								<template v-if="Array.isArray(row.col1)">
									<template
										v-for="(item, idx) in row.col1"
										:key="idx"
									>
										<AppPrice
											v-if="typeof item === 'object'"
											:amount="
												Object.values(item)[0] as number
											"
											:currency="
												(
													Object.keys(
														item,
													) as ResourceType[]
												)[0]
											"
											:resourceCheck="false"
										/>
										<AppPrice
											v-else-if="typeof item === 'number'"
											:amount="item"
											:resourceCheck="false"
										/>
										<span v-else>{{ item }}</span>
									</template>
								</template>
								<span v-else>{{ row.col1 }}</span>
							</div>
						</td>
						<td
							v-if="row?.col2"
							class="settlement-stats-table__value col2"
						>
							<div v-if="idx === 0">
								<div class="icon-tile-selector">
									<span>{{ row.col2 }}</span>
								</div>
							</div>
							<div v-else>
								<template v-if="Array.isArray(row.col2)">
									<template
										v-for="(item, idx) in row.col2"
										:key="idx"
									>
										<AppPrice
											v-if="typeof item === 'object'"
											:amount="
												Object.values(item)[0] as number
											"
											:currency="
												(
													Object.keys(
														item,
													) as ResourceType[]
												)[0]
											"
											:resourceCheck="false"
											:resourceChangeHighlight="
												(Object.values(
													item,
												)[0] as number) < 0 ||
												Object.keys(item)[0] ===
													ResourceType.FOOD_DIFF
													? ResourceChangeHighlight.NEGATIVE
													: ResourceChangeHighlight.POSITIVE
											"
											:resourceChangeSign="
												(Object.values(
													item,
												)[0] as number) < 0 ||
												Object.keys(item)[0] ===
													ResourceType.FOOD_DIFF
													? ResourceChangeHighlight.POSITIVE
													: null
											"
										/>
										<AppPrice
											v-else-if="typeof item === 'number'"
											:amount="item"
											:resourceCheck="false"
											:resourceChangeHighlight="
												item < 0
													? ResourceChangeHighlight.NEGATIVE
													: ResourceChangeHighlight.POSITIVE
											"
										/>
										<span v-else>{{ item }}</span>
									</template>
								</template>
								<span v-else>{{ row.col2 }}</span>
							</div>
						</td>
					</tr>
				</template>
			</Table>
			<div v-if="isMax" class="max-level-col">
				<div class="icon-trumpet" />
				<span class="max-level-col__label">{{
					translate("ui.max_level")
				}}</span>
			</div>
		</div>
		<div class="additional-section" v-if="false">
			<div class="additional-section__max" v-if="isMax">
				<p class="additional-section__max-heading">
					{{ translate("ui.town_maxed") }}.
				</p>
				<p class="additional-section__max-subheading">
					{{ translate("ui.more_levels_in_updates") }}.
				</p>
			</div>
			<div class="additional-section__base" v-else></div>
		</div>
	</div>
</template>

<script lang="ts" setup>
import AppPrice from "@/components/AppPrice.vue";
import EditableButton from "@/components/EditableButton.vue";
import SettlementNameEditPopup from "@/components/Settlement/SettlementNameEditPopup.vue";
import ShrinkText from "@/components/ShrinkText.vue";
import HorizontalDecorDivider from "@/components/SVG/HorizontalDecorDivider.vue";
import VerticalDecorHighlighter from "@/components/SVG/VerticalDecorHighlighter.vue";
import Table from "@/components/Table.vue";
import { GAME_TICKER_INTERVAL } from "@/config/constants";
import type SettlementCastleBuildingTileEntity from "@/game/entities/buildings/SettlementCastleBuildingTileEntity";
import { FrameName } from "@/game/entities/config";
import { ResourceType } from "@/game/entities/config/resources";
import { MAX_SETTLEMENT_LEVEL } from "@/game/entities/config/settlements";
import useEmitter, { EmitterEvents } from "@/hooks/useEmitter";
import useTileRepository from "@/hooks/useTileRepository";
import {
	PopupDictionary,
	PopupType,
	usePopupStore,
} from "@/store/usePopupStore";
import useSettlementsStore from "@/store/useSettlementsStore";
import { ResourceChangeHighlight, SkinTypes, TextAlign } from "@/types/ui";
import { translate } from "@/utils/translate";
import { computed, markRaw, onMounted, ref, useTemplateRef } from "vue";
import { useRoute } from "vue-router";

const route = useRoute();
const popupStore = usePopupStore();

const tilerepo = markRaw(useTileRepository());
const entity = tilerepo.getTileEntityByTileCoordinates({
	x: +route.params.x,
	y: +route.params.y,
}) as SettlementCastleBuildingTileEntity | null;
const fakeEntity = tilerepo.createEntityByFrameName(
	FrameName.SETTLEMENT_CASTLE,
	{
		x: +route.params.x,
		y: +route.params.y,
	},
) as SettlementCastleBuildingTileEntity | null;

const { setSettlementPropsByCoordinates } = useSettlementsStore();

const isMax = computed((): boolean => entity!.level >= MAX_SETTLEMENT_LEVEL);
fakeEntity!.level = (entity?.level ?? 1) + (isMax.value ? 0 : 1);

const settlementTable = useTemplateRef<HTMLElement>("settlementTable");
const dynamicTableDecoratorWidth = ref<number>(0);
const dynamicTableDecoratorHeight = ref<number>(0);
const dynamicTableDecorationInnerLeft = ref<number>(0);

const settlementName = ref<string>(entity!.settlementName);
let previousSettlementName = entity!.settlementName;

interface TableDataRow {
	name: string | null;
	col1: TableDataColumnType | null;
	col2?: TableDataColumnType | null;
}

type TableDataColumnType =
	| string
	| number
	| Array<string | number | { [key in ResourceType]?: number }>
	| null;

const gameTickerIntervalInSeconds = GAME_TICKER_INTERVAL / 1_000;

const tableData = computed((): TableDataRow[] => {
	const level = entity?.level ?? 1;

	const data: TableDataRow[] = [
		{ name: translate("ui.level"), col1: level, col2: level + 1 },
		{
			name: translate("ui.workforce"),
			col1: [
				{
					[ResourceType.POPULATION_DIFF]: entity?.production.get(
						ResourceType.POPULATION_INCOME,
					) as number,
				},
			],
			col2: [
				{
					[ResourceType.POPULATION_DIFF]:
						(fakeEntity?.production.get(
							ResourceType.POPULATION_INCOME,
						) as number) -
						(entity?.production.get(
							ResourceType.POPULATION_INCOME,
						) as number),
				},
			],
		},
		{
			name: translate("ui.regular_income"),
			col1: [
				{
					[ResourceType.COINS]: entity?.production.get(
						ResourceType.COINS_INCOME,
					) as number,
				},
				`/${gameTickerIntervalInSeconds}s`,
			],
			col2: [
				{
					[ResourceType.COINS]:
						(fakeEntity?.production.get(
							ResourceType.COINS_INCOME,
						) as number) -
						(entity?.production.get(
							ResourceType.COINS_INCOME,
						) as number),
				},
				`/${gameTickerIntervalInSeconds}s`,
			],
		},
		{
			name: translate("ui.income_per_click"),
			col1: [
				{
					[ResourceType.COINS]:
						entity?.getClickReward(1, true)?.[ResourceType.COINS] ??
						0,
				},
			],
			col2: [
				{
					[ResourceType.COINS]:
						(fakeEntity?.getClickReward(1, true)?.[
							ResourceType.COINS
						] ?? 0) -
						(entity?.getClickReward(1, true)?.[
							ResourceType.COINS
						] ?? 0),
				},
			],
		},
		{
			name: translate("ui.click_limit"),
			col1: [entity?.maxClicks || 0],
			col2: [(fakeEntity?.maxClicks || 0) - (entity?.maxClicks || 0)],
		},
		{
			name: translate("ui.food_consumption"),
			col1: [
				{
					[ResourceType.FOOD_DIFF]: entity?.consumption.get(
						ResourceType.FOOD_CONSUMPTION,
					),
				},
			],
			col2: [
				{
					[ResourceType.FOOD_DIFF]:
						(fakeEntity?.consumption.get(
							ResourceType.FOOD_CONSUMPTION,
						) || 0) -
						(entity?.consumption.get(
							ResourceType.FOOD_CONSUMPTION,
						) || 0),
				},
			],
		},
		{
			name: translate("ui.gravel_road_limit_cap_info"),
			col1: [entity?.roadCaps?.[FrameName.GRAVEL_ROAD] || 0],
			col2: [
				(fakeEntity?.roadCaps?.[FrameName.GRAVEL_ROAD] || 0) -
					(entity?.roadCaps?.[FrameName.GRAVEL_ROAD] || 0),
			],
		},
		{
			name: translate("ui.cobbled_road_limit_cap_info"),
			col1: [entity?.roadCaps?.[FrameName.COBBLED_ROAD] || 0],
			col2: [
				(fakeEntity?.roadCaps?.[FrameName.COBBLED_ROAD] || 0) -
					(entity?.roadCaps?.[FrameName.COBBLED_ROAD] || 0),
			],
		},
		{ name: null, col1: translate("ui.current_level"), col2: null },
	];

	if (isMax.value)
		return data.map(
			(row: TableDataRow): TableDataRow => ({
				name: row.name,
				col1: row.col1,
			}),
		);

	return data;
});

const saveNewSettlementName = (newName: string): void => {
	if (!newName) return;
	if (!entity) return;
	if (newName.length < 2 || newName.length > 20) return;
	if (previousSettlementName === newName) {
		return;
	}

	setSettlementPropsByCoordinates(
		{ x: entity.x, y: entity.y },
		{ name: newName },
	);

	previousSettlementName = newName;
	settlementName.value = newName;

	// IMPORTANT: redraw the main settlement label on the map
	if (entity.x === 512 && entity.y === 512) {
		useEmitter().emit(EmitterEvents.SettlementPropsUpdate);
	}
};

const openSettlementNameEditPopup = (): void => {
	popupStore.showPopup({
		id: PopupDictionary.SETTLEMENT_NAME,
		type: PopupType.INFO,
		heading: translate("ui.town_name"),
		skin: SkinTypes.LIGHT,
		height: "auto",
		isCentered: true,
		isPaddingless: true,
		dropOnInterrupt: true,
		component: markRaw(SettlementNameEditPopup),
		componentProps: {
			initialName: settlementName.value,
			onSave: saveNewSettlementName,
		},
	});
};

onMounted((): void => {
	setTimeout((): void => {
		const element = settlementTable.value;
		if (!element) return;

		const tr: HTMLElement | null = element.querySelector("tr");
		const td: HTMLElement | null =
			tr?.querySelector("td:nth-child(2)") ?? null;

		dynamicTableDecoratorHeight.value = element.offsetHeight;
		dynamicTableDecoratorWidth.value = td?.offsetWidth ?? 0;
		dynamicTableDecorationInnerLeft.value = td?.offsetLeft ?? 0;
	}, 500);
});
</script>

<style lang="scss" scoped>
.stats-settlement {
	@include flex-col-center;
	margin: 0 auto;
	width: calc(100% - 2.5rem);

	.settlement-name {
		margin: 1.25rem 0 1rem;
		width: 100%;
		@include flex-row-between;

		.label {
			margin-bottom: 0.5rem;
			color: #fff3dd;
			font-weight: 500;
			font-size: 0.875rem;
			text-align: left;
			text-shadow: 0.088rem 0.088rem 0 rgba(0, 0, 0, 0.6);
			text-transform: uppercase;
		}
	}

	.settlement-upgrades {
		margin: 0.75rem 0;
		@include flex-col-center;

		&__header {
			@include flex-row-center;
			gap: 0.25rem;

			span {
				padding-bottom: 0.25rem;
				color: #f2dcb5;
				font-weight: 800;
				font-size: 1rem;
				text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
				text-transform: uppercase;
			}
		}
	}

	.settlement-stats-table {
		flex: 1 1 auto;
		margin: 0 auto;
		border: none;
		width: 100%;

		&--is-max {
			.settlement-stats-table__name {
				width: 55%;
			}
		}

		&-wrapper {
			@include flex-row-stretch;
			position: relative;
			gap: 0.938rem;
			margin: 1rem 0;
			width: 100%;
		}

		tbody {
			tr {
				border-bottom: 0.125rem solid #4b4b50;
				height: 2.438rem;

				&:last-of-type {
					.settlement-stats-table__value {
						span {
							color: #fff3dd;
							font-weight: 800;
							line-height: 1.125rem;
						}
					}
				}
			}
		}

		&__name,
		&__value {
			padding: 0.25rem;
		}

		&__name {
			width: 40%;
			color: #fff3dd;
			font-size: 0.875rem;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}

		&__value {
			text-align: center;

			div {
				@include flex-row-center;
				flex-wrap: wrap;

				.icon-tile-selector {
					width: 1.875rem;
					height: 1.875rem;

					span {
						margin-top: -0.375rem;
						color: #fff3dd;
						font-weight: 800;
						font-size: 1.25rem;
						line-height: 1.25rem;
						text-shadow: 0.044rem 0.044rem 0.0625rem
							rgba(0, 0, 0, 0.6);
					}
				}
			}

			:deep(
				.price__value:not(
					.price--value-positive,
					.price--value-negative
				)
			) {
				color: #fff3dd;
			}

			&.col1 {
				:deep(.price__value) {
					font-weight: 800;
				}
			}

			&.col2 {
				:deep(.price__value) {
					font-weight: 500 !important;
				}
			}
		}
	}

	.max-level-col {
		@include flex-col-center;
		display: flex;
		flex-shrink: 0;
		width: 6.688rem;

		.icon-trumpet {
			filter: drop-shadow(0 0.125rem 0.125rem rgba(0, 0, 0, 0.6));
			width: 6.125rem;
			height: 6.438rem;
		}

		&__label {
			color: #fff3dd;
			font-size: 1.25rem;
			line-height: 1.75rem;
			text-align: center;
			text-shadow: 0.044rem 0.044rem 0.063rem rgba(0, 0, 0, 0.6);
		}
	}

	.additional-section {
		width: 100%;
		text-align: center;

		&__max {
			margin-bottom: 1.375rem;

			&-heading {
				color: #f4d278;
				font-size: 0.906rem;
				text-shadow: 0.088rem 0.088rem 0.125rem rgba(0, 0, 0, 0.6);
			}

			&-subheading {
				color: #f2dcb5;
				font-size: 0.813rem;
				text-shadow: 0.088rem 0.088rem 0.125rem rgba(0, 0, 0, 0.6);
			}
		}

		&__base {
			@include flex-row-center;
			margin-bottom: 1rem;
		}
	}

	:deep(.horizontal-decor-divider) {
		width: 100%;
	}
}
</style>
